import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { IProfile, ProfileModel } from '../models/profile.model';
import { IServerResponse } from '../models/server-response.model';
import { ToastService } from './toast.service';
import { UtilService } from './util.service';

/**
 * Catalogo de perfiles. El API solo devuelve los ACTIVOS, ya ordenados por
 * `description asc` (la etiqueta visible, en español), asi que el cliente
 * los consume tal cual llegan: no reordena nada.
 * Perfiles reales: 'sudo' y 'user'. NO existe 'admin'.
 */
@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);
  private readonly util = inject(UtilService);
  private readonly urlApi = `${environment.urlApi}/profiles`;

  readonly loading = signal(false);
  readonly profiles = signal<ProfileModel[]>([]);

  async getProfiles(): Promise<ProfileModel[]> {
    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ profiles: IProfile[] }>>(this.urlApi),
      );

      if (response.statusCode !== 200) {
        this.profiles.set([]);
        this.toast.error(response.message);
        return [];
      }

      const profiles = (response.data?.profiles ?? []).map(
        (profile) => new ProfileModel(profile),
      );
      this.profiles.set(profiles);
      return profiles;
    } catch (error) {
      this.profiles.set([]);
      this.toast.error(this.util.errorMessage(error, 'No fue posible cargar los perfiles.'));
      return [];
    } finally {
      this.loading.set(false);
    }
  }

  /** Carga el catalogo una sola vez por sesion de la consola. */
  async ensureProfiles(): Promise<ProfileModel[]> {
    if (this.profiles().length > 0) return this.profiles();
    return this.getProfiles();
  }
}
